import { UeElement } from '@oneaudi/falcon-tools';
import { TextLink, TextButton, ButtonGroup } from '@oneaudi/unified-web-components';
import React, { useCallback } from 'react';
import { LayerContentHTML } from '@oneaudi/fa-one-layer';
import { FocusLayerSizeV2, LayerManagerV27 } from '@volkswagen-onehub/layer-manager';
import styled from 'styled-components';
import { Alignment, ButtonItem, ModuleType } from '../../types';
import { setHeadlessUrl } from '../utils/setHeadlessUrl';
import { useTracking } from '../hooks/useTracking';

interface EditorialCTAProps {
  mode: ModuleType;
  cta: ButtonItem;
  index: number;
  alignment: Alignment;
  layerManager?: LayerManagerV27;
}

interface StyledButtonGroupProps {
  alignment: Alignment;
}

const StyledButtonGroup = styled(ButtonGroup)<StyledButtonGroupProps>`
  justify-content: ${(props) => props.alignment}!important;
`;

export const EditorialCTA: React.FC<EditorialCTAProps> = ({
  mode,
  cta,
  index,
  layerManager,
  alignment,
}) => {
  const hasValidCTA = cta.label?.length > 0 && cta.url?.length > 0;
  if (!hasValidCTA) return null;
  const tracking = useTracking({
    implementer: 2,
  });
  const handleOpenInLayer = useCallback((url: string) => {
    // eslint-disable-next-line react/destructuring-assignment
    layerManager?.openFocusLayer(
      () => <LayerContentHTML enableDeepLinking url={`${setHeadlessUrl(url)}`} />,
      {},
      {
        userCloseable: true,
        size: FocusLayerSizeV2.A,
      },
    );
  }, []);

  const trackClickEvent = () => {
    return tracking.sendClickEvent(
      cta.label,
      cta.url,
      cta.variant === 'text' ? 'text link' : 'cta',
      cta.variant === 'text' ? 'text link' : 'button',
      index + 1,
    );
  };
  return (
    <StyledButtonGroup mode="hug" alignment={alignment}>
      {cta.variant === 'text' && mode !== ModuleType.NUMBERS ? (
        <TextLink
          aria-label={cta.ariaLabel || cta.label}
          variant="standalone"
          icon="forward"
          iconPosition="trailing"
          href={cta.url}
          target={cta.openInTabOrLayer === 'tab' ? '_blank' : '_self'}
          onClick={(e) => {
            if (cta?.openInTabOrLayer === 'layer') {
              e.preventDefault();
              handleOpenInLayer(cta.url);
            }
            trackClickEvent();
          }}
          size="sm"
          data-testid="text-component-button"
          margin={['var(--spacing-relative-lg)', 0, 0, 0]}
        >
          <UeElement type="text" property="cta_label" label="Label">
            {cta.label}
          </UeElement>
        </TextLink>
      ) : (
        <TextButton
          aria-label={cta.ariaLabel || cta.label}
          target={cta.openInTabOrLayer === 'tab' ? '_blank' : '_self'}
          isFullWidth
          variant="secondary"
          href={cta.url}
          onClick={(e) => {
            if (cta?.openInTabOrLayer === 'layer') {
              e.preventDefault();
              handleOpenInLayer(cta.url);
            }
            trackClickEvent();
          }}
          size={process.env.IS_LEGACY ? 'md' : 'sm'}
          data-testid="text-component-button"
          margin={['var(--spacing-relative-lg)', 0, 0, 0]}
        >
          <UeElement type="text" property="cta_label" label="Label">
            {cta.label}
          </UeElement>
        </TextButton>
      )}
      <span style={{ display: 'none' }} />
    </StyledButtonGroup>
  );
};
